import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ConfirmationModal from '../components/ConfirmationModal';
import RegisteredEventHeroCard from '../components/RegisteredEventHeroCard';

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');

function CancelRegistration() {
  const [searchParams] = useSearchParams();
  // `p` is the cancel capability: unguessable cancel_token (new) or legacy participant ObjectId.
  const cancelCapability = (searchParams.get('p') || '').trim();
  const [status, setStatus] = useState('ready'); // 'ready' | 'loading' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [eventInfo, setEventInfo] = useState(null);
  const [attendance, setAttendance] = useState('');

  const serverUrl = uiConfig.serverInfo?.serverUrl || '';

  useEffect(() => {
    if (!cancelCapability) {
      setStatus('error');
      setMessage('Invalid link. No registration token provided.');
      return;
    }
    if (!serverUrl) {
      setStatus('error');
      setMessage('Configuration error. Please try again later.');
      return;
    }
    setStatus('ready');
    setMessage('');
    setDetailsError('');
  }, [cancelCapability, serverUrl]);

  const loadCancelLinkInfo = useCallback(async () => {
    if (!cancelCapability || !serverUrl) return;
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const res = await apiFetch(serverUrl + '/getCancelLinkInfo', {
        method: 'POST',
        body: JSON.stringify({ cancel_token: cancelCapability }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data || !data.success) {
        throw new Error(data.error || 'Unable to load event details.');
      }
      setEventInfo(data.event || null);
      setAttendance((data.participant && data.participant.attendance) || '');
    } catch (err) {
      setEventInfo(null);
      setDetailsError(getMessageForFetchError(err));
    } finally {
      setDetailsLoading(false);
    }
  }, [cancelCapability, serverUrl]);

  useEffect(() => {
    loadCancelLinkInfo();
  }, [loadCancelLinkInfo]);

  const canAttemptCancel = useMemo(() => {
    return !!cancelCapability && !!serverUrl && status !== 'loading' && status !== 'success';
  }, [cancelCapability, serverUrl, status]);

  async function doCancel() {
    if (!cancelCapability || !serverUrl) return;
    setStatus('loading');
    setMessage('');
    const res = await apiFetch(serverUrl + '/cancelByLink', {
      method: 'POST',
      body: JSON.stringify({ cancel_token: cancelCapability }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data && data.success) {
      setStatus('success');
      setMessage(data.message || 'Your registration has been cancelled successfully. Your spot may be offered to someone else.');
      setAttendance('Cancelled');
      loadCancelLinkInfo();
      return;
    }
    setStatus('error');
    setMessage(data.error || 'Unable to cancel registration. The link may have expired or the registration was already cancelled.');
  }

  const alreadyCancelled = (attendance || '').trim().toLowerCase() === 'cancelled';

  return (
    <div style={{ minHeight: '100%', background: '#f5f5f5' }}>
      <header className="page-hero">
        <h1>Cancel registration</h1>
      </header>
      <div className="page-container" style={{ flex: 1, minHeight: 0 }}>
      <ConfirmationModal
        open={confirmOpen}
        title="Cancel registration"
        message="Cancel your registration for this event? You can register again later if the event is still open."
        mode="confirm"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          await doCancel();
          setConfirmOpen(false);
        }}
      />

      <div style={{ paddingBottom: 28 }}>
        {detailsLoading && (
          <div className="empty-message" aria-live="polite">
            Loading registration…
          </div>
        )}
        {detailsError && (
          <div style={{ padding: '12px 16px', marginBottom: 8, background: '#f8d7da', color: '#842029', borderRadius: 4, fontSize: '0.875rem' }}>
            {detailsError}
          </div>
        )}

        {status === 'loading' && <p style={{ color: '#666' }}>Waiting for confirmation…</p>}
        {status === 'success' && (
          <p style={{ color: '#0f5132', marginBottom: '16px' }}>{message}</p>
        )}
        {status === 'error' && (
          <p style={{ color: '#842029', marginBottom: '16px' }}>{message}</p>
        )}
        {alreadyCancelled && status !== 'error' && status !== 'loading' && (
          <p style={{ color: '#0f5132', marginBottom: '16px' }}>
            This registration is already cancelled.
          </p>
        )}

        <p style={{ fontSize: '14px', color: '#6c757d' }}>
          You can close this page or return to the <Link to="/EventCalendar">Event Calendar</Link> to register for other events.
        </p>

        <div className="cards-wrapper" style={{ marginTop: 12 }}>
          {eventInfo && (
            <RegisteredEventHeroCard
              event={{ ...eventInfo, attendance: attendance || eventInfo.attendance || "" }}
              onCancel={() => setConfirmOpen(true)}
              cancelling={status === 'loading'}
              cancelDisabled={!canAttemptCancel || alreadyCancelled}
              cancelLabel={alreadyCancelled ? 'CANCELLED' : 'CANCEL REGISTRATION'}
              accountId={eventInfo.account_id != null ? String(eventInfo.account_id) : ''}
              serverUrl={serverUrl}
            />
          )}
        </div>
      </div>
      </div>
    </div>
  );
}

export default CancelRegistration;
